import { SplitFieldRequestBody, SplitFieldResponse, axios } from "utils";

import { PUBLIC_FIELDS_URL as endpoint } from "consts";

import {
  AddFieldPracticeParticipationsRequest,
  AddFieldPracticeParticipationsResponseBody,
  AddFieldRequestBody,
  BasicFieldDetails,
  BulkCropPracticesRequest,
  BulkCropPracticesResponse,
  BulkUpdateCropDatesRequestBody,
  BulkUpdateCropDatesResponseBody,
  BulkUpdateCropYieldRequestBody,
  BulkUpdateCropYieldResponseBody,
  BulkUpdateMineralFertilizerPracticesRequestBody,
  BulkUpdateOrganicFertilizerPracticesRequestBody,
  BulkUpdateSeedingPracticesRequestBody,
  BulkUpdateTillagePracticesRequestBody,
  CropPracticesRequestBody,
  CropPracticesResponse,
  CropRotationResult,
  EVALUATION_METRICS,
  FieldCropRecommendationsResponse,
  FieldDetails,
  FieldEvaluationMetricCorrectionsResponse,
  FieldListField,
  FieldPracticeFilterOptionsResponse,
  FieldPracticeResponse,
  FieldPracticeRowTimestamps,
  FieldsListResponse,
  ResolveFieldGeometryResponse,
} from "models";

export const getFields = async (data: { signal: AbortSignal }): Promise<FieldsListResponse> => axios.get(`${endpoint}`, { signal: data.signal });

export const getHaInConversion = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/ha-in-conversion`, { data });

export const getFieldCount = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/count`, { data });

export const getRedzoneFieldCount = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/redzone-field-count`, { data });

export const getFieldBasicDetails = async (fieldId: string, includeDisabledFields?: boolean): Promise<BasicFieldDetails> =>
  axios.get(`${endpoint}/${fieldId}/basic-details${includeDisabledFields ? "?includeDisabledFields=true" : ""}`);

export const getField = async (data: { id: string; includeDisabledFields?: boolean; signal: AbortSignal }): Promise<FieldDetails> =>
  axios.get(`${endpoint}/${data.id}${data.includeDisabledFields ? "?includeDisabledFields=true" : ""}`, { signal: data.signal });

export const uploadFields = async (data: { file: string }) => axios.post(`${endpoint}/upload`, { data });

export type UploadedField = { id: string; [key: string]: unknown };

export type UploadFieldsExperimentRequestBody = {
  file: string;
  seasonYears?: number[];
  waitForSoilData?: boolean;
  triggerRemoteSensing?: boolean;
};

export const uploadFieldsExperiment = async (data: UploadFieldsExperimentRequestBody): Promise<UploadedField[]> =>
  axios.post(`${endpoint}/upload-experiment`, { data }, { timeout: 120000 });

export type FieldCropDetectionStatus = "pending" | "waiting_for_soil_data" | "waiting_for_dependencies" | "processing" | "completed" | "failed" | "skipped";

export type FieldCropDetection = {
  id: string;
  fieldId: string;
  seasonYear: number;
  status: FieldCropDetectionStatus;
  detectionType: "main_crop" | "cover_crop";
};

export const getFieldDetections = async (fieldId: string): Promise<FieldCropDetection[]> => axios.get(`${endpoint}/${fieldId}/detections`);

export type CoverCropFarmerDecision = "confirmed" | "confirmed_absent";

export type CoverCropMixComplexity = "simple" | "complex";

export type PendingCoverCropSuggestion = {
  detectionId: string;
  fieldId: string;
  fieldName: string;
  seasonYear: number;
  farmerDecision: CoverCropFarmerDecision | null;
  coverCropProbability: number | null;
  modelThreshold: number | null;
  rotationType: string | null;
  obsWindowStart: string | null;
  obsWindowSource: string | null;
  missingKeyFeatures: string[];
  detectedAt: string | null;
};

export type CoverCropDecision = {
  detectionId: string;
  decision: CoverCropFarmerDecision;
  mixType?: CoverCropMixComplexity;
  seedingStart?: string;
  seedingEnd?: string;
  harvestStart?: string;
  harvestEnd?: string;
  hasResidue?: boolean;
};

export type CoverCropDecisionOutcome = {
  detectionId: string;
  outcome: "created" | "recorded" | "skipped" | "error";
  reason?: string;
};

export const getPendingCoverCropDetections = async (): Promise<PendingCoverCropSuggestion[]> => axios.get(`${endpoint}/detections`);

export const decideCoverCropDetections = async (data: { decisions: CoverCropDecision[] }): Promise<{ results: CoverCropDecisionOutcome[] }> =>
  axios.put(
    `${endpoint}/detections/decisions`,
    { data },
    {
      // @ts-expect-error custom params can be passed in axios config
      hideErrorMessage: true,
    }
  );

export const addField = async ({ signal, ...data }: { signal: AbortSignal } & AddFieldRequestBody) => axios.post(`${endpoint}`, data, { signal });

export const updateField = async (data: { id: string; signal: AbortSignal }) => axios.put(`${endpoint}/${data.id}`, { data, signal: data.signal });

export const deleteMultipleFields = async (data: { fieldIds: string[] }) => axios.delete(`${endpoint}`, { data });

export const deleteField = async (data: { fieldId: string }) => axios.delete(`${endpoint}/${data.fieldId}`);

export const splitField = async (data: SplitFieldRequestBody): Promise<SplitFieldResponse> =>
  axios.post(
    `${endpoint}/${data.fieldId}/split`,
    { data },
    {
      // @ts-expect-error custom params can be passed in axios config
      hideErrorMessage: true,
    }
  );

// Stores the field's region/redzone when it has none yet; no-op once it has one.

export const resolveFieldGeometry = async (fieldId: string): Promise<ResolveFieldGeometryResponse> => axios.post(`${endpoint}/${fieldId}/geometry`);

export const getFieldListByFarmId = async ({ farmId }: { farmId: string }): Promise<FieldListField[]> => axios.get(`${endpoint}/list?farmId=${farmId}`);

export const getFarmFieldListAtPointInTime = async ({ farmId, date }: { farmId: string; date: string }): Promise<FieldListField[]> =>
  axios.get(`${endpoint}/list?farmId=${farmId}&date=${date}`);

export const getFieldPractices = async (data: { fieldId: string }): Promise<FieldPracticeResponse> => axios.get(`${endpoint}/${data.fieldId}/practices`);

export const getFieldPracticeTimestamps = async (data: { fieldId: string }): Promise<FieldPracticeRowTimestamps[]> =>
  axios.get(`${endpoint}/${data.fieldId}/practice-timestamps`);

export const getFieldCropRotation = async (data: { fieldId: string }): Promise<CropRotationResult> => axios.get(`${endpoint}/${data.fieldId}/crop-rotation`);

export const getFieldPracticesBulk = async ({ fieldIds, signal }: { fieldIds: string[]; signal: AbortSignal }): Promise<FieldPracticeResponse[]> =>
  axios.post(`${endpoint}/practices`, { data: { fieldIds }, signal });

export const addFieldCropPractice = async ({
  fieldId,
  requestBody,
}: {
  fieldId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.post(`${endpoint}/${fieldId}/crops`, { data: requestBody });

export const updateFieldCropPractice = async ({
  fieldId,
  cropId,
  requestBody,
}: {
  fieldId: string;
  cropId: string;
  requestBody: CropPracticesRequestBody;
}): Promise<CropPracticesResponse> => axios.put(`${endpoint}/${fieldId}/crops/${cropId}`, { data: requestBody });

export const deleteFieldCropPractice = async ({ fieldId, cropId }: { fieldId: string; cropId: string }): Promise<CropPracticesResponse> =>
  axios.delete(`${endpoint}/${fieldId}/crops/${cropId}`);

export const getFieldCropRecommendations = async (data: { fieldIds: string[] }): Promise<FieldCropRecommendationsResponse> =>
  axios.post(`${endpoint}/crop-recommendations`, { fieldIds: data.fieldIds });

export const addFieldPracticeParticipations = async (data: AddFieldPracticeParticipationsRequest[]): Promise<AddFieldPracticeParticipationsResponseBody> =>
  axios.post(`${endpoint}/practice-participations`, data);

export const deleteFieldPracticeParticipations = async (data: { fieldId: string; practiceSlugs: string[] }) =>
  axios.delete(`${endpoint}/practice-participations`, { data: data });

export const cancelFieldReactivation = async (fieldId: string): Promise<{ success: boolean }> =>
  axios.post(`${endpoint}/${fieldId}/practice-participations/cancel-reactivation`);

export const getFieldEvaluationMetricCorrections = async (
  data: { fieldIds: string[] },
  signal: AbortSignal
): Promise<FieldEvaluationMetricCorrectionsResponse> => axios.post(`${endpoint}/evaluation-metric-corrections`, { fieldIds: data.fieldIds }, { signal });

export const addOrUpdateFieldEvaluationMetricCorrection = async ({
  fieldId,
  data,
  correctionId,
}: {
  fieldId: string;
  data: { year: number; value: number; metric: EVALUATION_METRICS };
  correctionId?: string;
}): Promise<{ id: string }> => {
  if (correctionId) {
    return axios.put(`${endpoint}/${fieldId}/evaluation-metric-corrections/${correctionId}`, data);
  }

  return axios.post(`${endpoint}/${fieldId}/evaluation-metric-corrections`, data);
};

export const deleteFieldEvaluationMetricCorrection = async (data: { fieldId: string; correctionId: string }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/${data.fieldId}/evaluation-metric-corrections/${data.correctionId}`);

export const updateBulkFieldCropPractice = async ({ data }: { data: BulkCropPracticesRequest[] }): Promise<BulkCropPracticesResponse> =>
  axios.put(`${endpoint}/crops/bulk`, { data });

export const getFieldPracticeFilterOptions = async (): Promise<FieldPracticeFilterOptionsResponse> => axios.get(`${endpoint}/practice-filter-options`);

export const getPracticeFilteredFieldIds = async (data: { cropIds?: string[]; years?: number[] }): Promise<{ fieldIds: string[] }> =>
  axios.post(`${endpoint}/practice-filtered-fields`, {
    cropIds: data.cropIds || [],
    years: data.years || [],
  });

export const bulkUpdateCropYield = async (data: BulkUpdateCropYieldRequestBody[]): Promise<BulkUpdateCropYieldResponseBody> =>
  axios.put(`${endpoint}/crops/bulk-update-yield`, { data });

export const bulkUpdateSeedingPractice = async (data: BulkUpdateSeedingPracticesRequestBody): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/crops/bulk-update-seeding-practices`, { data });

export const bulkUpdateOrganicFertilizerPractice = async (data: BulkUpdateOrganicFertilizerPracticesRequestBody): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/crops/bulk-update-organic-fertilizer-practices`, { data });

export const bulkUpdateMineralFertilizerPractice = async (data: BulkUpdateMineralFertilizerPracticesRequestBody): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/crops/bulk-update-mineral-fertilizer-practices`, { data });

export const bulkUpdateTillagePractice = async (data: BulkUpdateTillagePracticesRequestBody): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/crops/bulk-update-tillage-practices`, { data });

export const bulkUpdateCropDates = async (data: BulkUpdateCropDatesRequestBody): Promise<BulkUpdateCropDatesResponseBody> =>
  axios.put(`${endpoint}/crops/bulk-update-crop-dates`, data);
